/* eslint-disable max-lines -- Pre-existing violation */
import {isFeatureEnabled} from 'feature-flags';
import {
  Action,
  Alert,
  Card,
  closeDialog,
  closeDropdown,
  DeleteDialog,
  DropdownItem,
  DropdownItemProps,
  openDeleteDialog,
  openDialog,
  openDropdown,
  showNotification,
  Switch,
  Upload,
  useDialog,
} from 'platform/components';
import {Box, Center, Hide, HStack, Icon, Show, Heading, VStack, Space} from 'platform/foundation';
import {Lightbox, LightboxAction, useLightbox} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {memo, useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {filter, isEmpty, isNil, reject, flatten, values} from 'ramda';
import {isFalse, isNilOrEmpty, isNotNil, isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetParticipationQuery, useGetPromotionalPhotosQuery} from '@dms/api/core';
import {useGenerateVgpCertificateMutation, useGetWatermarkListQuery} from '@dms/api/sales';
import {EntityResourceIds, VehiclePhotoCategory} from '@dms/api/shared';
import {
  saleVehiclePhotoApi,
  type PhotoCheckerPhotoEvaluationSaleVehiclePhoto,
  useGetPromoPhotosForSaleVehicleQuery,
  useGetSaleVehicleAlbumPhotoGuideQuery,
  useGetSaleVehicleAlbumPhotoListQuery,
  useGetSaleVehicleFromSaleVehicleIdQuery,
  usePatchSaleVehiclePhotosCategoryMutation,
  usePatchSaleVehicleWatermarkV2Mutation,
} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {buildSortableItemId} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {
  BackgroundRemovalDialog,
  useBatchDownload,
  usePermissions,
  handleApiError,
  useDmsLightboxActionsFactory,
} from '@dms/shared';
import {DownloadDialog} from '@dms/teas';

import {
  ApiError,
  buildArray,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
  useBoolean,
} from 'shared/web';

import {IMAGE_SIZE} from '../../../constants/imageSize';
import {useSaleVehicleAlbum} from '../../../hooks/useSaleVehicleAlbum';
import {useVehicleAlbumImageSelection} from '../../../hooks/useVehicleAlbumImageSelection';
import {useFinancingPromoStatus} from '../hooks/useFinancingPromoStatus';
import {usePhotoGuideActions} from '../hooks/usePhotoGuideActions';
import {useVehicleAlbumDndManager} from '../hooks/useVehicleAlbumDndManager';
import {useVgpCertificateAvailability} from '../hooks/useVgpCertificateAvailability';
import {RenderAlbumImage} from '../types/RenderAlbumImage';
import {UnifiedAlbumImage} from '../types/UnifiedAlbumImage';
import {buildFinancingPlaceholders} from '../utils/buildFinancingPlaceholders';
import {getDirtyVehicleImages} from '../utils/getDirtyVehicleImages';
import {getOwnPromoPhotoPositions} from '../utils/getOwnPromoPhotoPositions';
import {isFinancingPromoPhoto} from '../utils/isFinancingPromoPhoto';
import {isImageProcessing} from '../utils/isImageProcessing';
import {isPromoTileImage} from '../utils/isPromoTileImage';
import {matchPhotoEval} from '../utils/matchPhotoEval';
import {mergeAlbumAndPromoImages} from '../utils/mergeAlbumAndPromoImages';
import {prepareAlbumImageUrl} from '../utils/prepareAlbumImageUrl';
import {sortImages} from '../utils/sortImages';
import {splitEffectivePromoPhotos} from '../utils/splitEffectivePromoPhotos';
import {toUnifiedAlbumImage} from '../utils/toUnifiedAlbumImage';
import {AlbumImage} from './AlbumImage';
import {AlbumPhotoGrid} from './AlbumPhotoGrid';
import {FinancingPlaceholderTile} from './FinancingPlaceholderTile';
import {GuidedAlbumContent} from './GuidedAlbumContent';
import {PhotoGuideContextMenuItems} from './PhotoGuideContextMenuItems';
import {PhotoGuideMissingRequiredFlag} from './PhotoGuideMissingRequiredFlag';
import {VehicleWatermarkChoice} from './VehicleWatermarkChoice';

// The default toast omits validationErrors, e.g. the BE watermark count limit.
const handleWatermarkPatchError = (error: ApiError) =>
  handleApiError(error, {
    silentOnValidationError: true,
    callback: ({validationErrors}) => {
      if (isNotNil(validationErrors)) {
        showNotification.error(validationErrors.map(({message}) => message).join(' '));
      }
    },
  });

interface AlbumProps extends RequiredTestIdProps {
  title: string;
  vehicleId: string;
  saleVehicleId: string;
  saleVehicleAlbumId: string;
  isActiveSaleVehicle: boolean;
  canUpload?: boolean;
  canDelete?: boolean;
  showPromotionalPhotos?: boolean;
  globallySelectedImagesIds?: Record<string, string[]>;
  onBackgroundRemoval?: (
    albumId: string,
    isInProgress: boolean,
    errorFileOperationIds?: string[]
  ) => void;
  buildAlbumActionsContextMenu?: (payload: {
    sourceAlbumId: string;
    isSalesAlbum: boolean;
    images: string[];
    isProcessing: boolean;
    onSuccess?: () => void;
    onEventFired?: () => void;
  }) => React.ReactNode;
  onUpdateGlobalImageSelection?: (albumId: string, imageIds: string[]) => void;
  onReusePreviousSalePhotos?: () => void;
  isReusingPreviousSalePhotos?: boolean;
  canApplyWatermark?: boolean;
  inactiveAlbumIds?: string[];
  isSalesAlbum?: boolean;
  onAlbumChange?: () => void;
  photoEvalMap?: Record<string, PhotoCheckerPhotoEvaluationSaleVehiclePhoto>;
  createdAt?: string;
  // A gallery change makes the BE re-render the financing promo cards; see useFinancingRenderWindow.
  isFinancingRenderWindowOpen?: boolean;
  startFinancingRenderWindow?: () => void;
}

const ALBUM_IMAGE_CONTEXT_MENU_ID = 'album-image-context-menu';

const ALLOWED_MIME_TYPES_TO_UPLOAD = ['image/jpeg', 'image/png', 'image/jpg'].join(', ');

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
function AlbumComponent(props: AlbumProps) {
  const dispatch = useDispatch();
  const lightboxActionsFactory = useDmsLightboxActionsFactory();
  const formatDateTime = useDateTimeFormatter();

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const {data: saleVehicle} = useGetSaleVehicleFromSaleVehicleIdQuery(
    {
      vehicleId: props.saleVehicleId,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [
    isLightboxDeleteDialogOpen,
    openLightboxDeleteDialog,
    closeLightboxDeleteDialog,
    {storage: imageToDeleteId},
  ] = useDialog<string>();

  const [
    canRemoveVehiclePhotoBackground,
    canUploadVehicleAlbumPhotos,
    canDeleteVehicleAlbumPhoto,
    canRotateVehiclePhoto,
    canMoveVehiclePhoto,
    canSaleVehicleBackgroundRemoval,
    canReadPhotoCheckerReport,
  ] = usePermissions({
    permissionKeys: [
      'removeVehiclePhotoBackground',
      'createVehicleAlbumPhotos',
      'deleteVehicleAlbumPhoto',
      'rotateVehiclePhoto',
      'moveVehiclePhoto',
      'saleVehicleBackgroundRemoval',
      'readPhotoCheckerReport',
    ],
    scopes: {
      createVehicleAlbumPhotos: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      deleteVehicleAlbumPhoto: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      rotateVehiclePhoto: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      moveVehiclePhoto: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      removeVehiclePhotoBackground: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
    },
  });

  const canRemoveBackground = canRemoveVehiclePhotoBackground && canSaleVehicleBackgroundRemoval;

  const canEditPhotos = canRotateVehiclePhoto && canMoveVehiclePhoto;

  const isUploadAllowed = canUploadVehicleAlbumPhotos && props.canUpload;

  const photoGuideActions = usePhotoGuideActions({
    saleVehicleId: props.saleVehicleId,
    saleVehicleAlbumId: props.saleVehicleAlbumId,
    onSuccess: () => {
      props.startFinancingRenderWindow?.();
      props.onAlbumChange?.();
    },
  });

  const [patchSaleVehiclePhotosCategory] = usePatchSaleVehiclePhotosCategoryMutation();

  const {isVgpCertificateAvailable} = useVgpCertificateAvailability({
    vehicleId: props.vehicleId,
  });
  const [generateVgpCertificate, {isLoading: isGeneratingVgpCertificate}] =
    useGenerateVgpCertificateMutation();

  const {
    handleSaleVehicleDownload,
    isLoading: isBatchDownloadLoading,
    isStatePending: isBatchStatePending,
  } = useBatchDownload();

  const {data: watermarks} = useGetWatermarkListQuery();
  const [patchWatermark, {isLoading: isPatchingWatermark}] =
    usePatchSaleVehicleWatermarkV2Mutation();

  // Shift-selection spans from the last clicked photo within the list the shift-click lands in;
  // the guided view renders several lists (layout, Backlog) whose arrays change every render,
  // so the anchor is the photo id, resolved in the clicked list.
  const [shiftSelectionAnchorId, setShiftSelectionAnchorId] = useState<string | null>(null);

  const selectedWatermarkIds = saleVehicle?.editableWatermarkIds ?? [];
  const shouldApplyWatermark = isNotNilOrEmpty(selectedWatermarkIds);

  useEffect(
    function closeDownloadDialogWhenBatchIdle() {
      if (!isBatchDownloadLoading && !isBatchStatePending) {
        closeDialog('download-dialog');
      }
    },
    [isBatchStatePending, isBatchDownloadLoading]
  );

  const saleVehicleAlbum = useSaleVehicleAlbum({
    saleVehicleAlbumId: props.saleVehicleAlbumId,
    saleVehicleId: props.saleVehicleId,
    onMutationSuccess: props.startFinancingRenderWindow,
  });

  const albumId = props.saleVehicleAlbumId;

  // useCallback is required for correctness: passed as a useEffect dep inside
  // useVehicleAlbumImageSelection — an inline function would re-trigger that effect
  // every render, calling setState in the parent and causing an infinite loop.
  const handleImageSelectionChange = useCallback(
    (imageIds: string[]) => props.onUpdateGlobalImageSelection?.(albumId, imageIds),
    [props.onUpdateGlobalImageSelection, albumId] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const imageSelection = useVehicleAlbumImageSelection({
    onSelectionChange: handleImageSelectionChange,
  });

  const [lightboxControls, {onOpen}] = useLightbox(props.saleVehicleAlbumId);

  const [imagesToRender, setImagesToRender] = useState<UnifiedAlbumImage[]>([]);
  const renderedImagesRef = useRef<UnifiedAlbumImage[]>([]);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [areBulkActionsVisible, showBulkActions, hideBulkActions] = useBoolean();

  const {
    data: albumImages,
    isLoading: isLoadingAlbumImages,
    isFetching: isFetchingAlbumImages,
    isError: hasAlbumImagesError,
    fulfilledTimeStamp: albumImagesFetchedAt,
  } = useGetSaleVehicleAlbumPhotoListQuery(
    {
      saleVehicleId: props.saleVehicleId,
      saleVehicleAlbumId: props.saleVehicleAlbumId,
    },
    {
      skip: isNilOrEmpty(props.saleVehicleId) || isNilOrEmpty(props.saleVehicleAlbumId),
    }
  );

  const {
    data: promotionalPhotos,
    isLoading: isLoadingPromotionalPhotos,
    isFetching: isFetchingPromotionalPhotos,
    isError: hasPromotionalPhotosError,
  } = useGetPromoPhotosForSaleVehicleQuery(
    {
      saleVehicleId: props.saleVehicleId,
    },
    {
      skip: isNilOrEmpty(props.saleVehicleId) || !props.showPromotionalPhotos,
    }
  );

  const isFinancingPromoEnabled =
    isFeatureEnabled(featureFlags.FINANCING_PROMO_PHOTOS) && Boolean(props.showPromotionalPhotos);
  const financingPromoStatus = useFinancingPromoStatus({
    saleVehicleId: props.saleVehicleId,
    isEnabled: isNotNilOrEmpty(props.saleVehicleId) && isFinancingPromoEnabled,
    isRenderWindowOpen: props.isFinancingRenderWindowOpen ?? false,
  });
  // The catalog only adds the source photo number to the placeholder text of a missing photo.
  const {data: promoPhotoConfigs} = useGetPromotionalPhotosQuery(undefined, {
    skip: !isFinancingPromoEnabled,
  });

  // The Photo Guide view applies to the active Sales Album only, behind its feature flag and the
  // tenant's setting. Every other album, and the flag-off path, render the flat grid as before.
  const isPhotoGuideCandidate =
    props.isSalesAlbum === true &&
    props.isActiveSaleVehicle &&
    isFeatureEnabled(featureFlags.SALES_PHOTO_GUIDE) &&
    isNotNilOrEmpty(props.saleVehicleId) &&
    isNotNilOrEmpty(props.saleVehicleAlbumId);
  const {
    data: photoGuide,
    isLoading: isLoadingPhotoGuide,
    isFetching: isFetchingPhotoGuide,
    isError: hasPhotoGuideError,
    fulfilledTimeStamp: photoGuideFetchedAt,
  } = useGetSaleVehicleAlbumPhotoGuideQuery(
    {saleVehicleId: props.saleVehicleId, saleVehicleAlbumId: props.saleVehicleAlbumId},
    {skip: !isPhotoGuideCandidate}
  );
  const isGuided = isPhotoGuideCandidate && photoGuide?.enabled === true;
  // The flat grid waits for the guide so a guided album does not flash the flat grid first.
  const isLoadingAlbumContent =
    isLoadingAlbumImages ||
    isLoadingPromotionalPhotos ||
    (isPhotoGuideCandidate && isLoadingPhotoGuide);
  // A failed guide request shows the error state, not the flat grid of a guided album.
  const hasAlbumContentError =
    hasAlbumImagesError ||
    hasPromotionalPhotosError ||
    (isPhotoGuideCandidate && hasPhotoGuideError);
  // Guide mutations move photos, so they need the same rights as moving a photo in the flat album.
  const canEditPhotoGuide = isGuided && canEditPhotos && props.canUpload === true;
  const missingRequiredPhotoCount = photoGuide?.missingRequiredPhotoTypes.length ?? 0;
  // The guided view reads the album photos straight from the query, so the layout and the photos
  // it is joined with always come from the same render; only the promo tiles are merged ones.
  const guidedImages = isGuided
    ? [
        ...imagesToRender.filter(isPromoTileImage),
        ...sortImages((albumImages ?? []).map(toUnifiedAlbumImage)),
      ]
    : [];

  const isPhotoCheckerEnabled = canReadPhotoCheckerReport && props.isSalesAlbum;

  const handleUpdate = (images: UnifiedAlbumImage[]) => {
    setImagesToRender(images);
  };

  const handleEnd = async (
    images: UnifiedAlbumImage[],
    sourceAlbum: string,
    preferCopy?: boolean
  ) => {
    const positions = getOwnPromoPhotoPositions(renderedImagesRef.current, images);
    const isPromoMoved = isNotNil(positions)
      ? await saleVehicleAlbum.actions.movePromotionalImages(positions)
      : false;

    const vehicleImages = getDirtyVehicleImages(images);

    // An empty album move is a real album change on the BE and demotes financing promo cards.
    if (isEmpty(vehicleImages)) {
      if (isPromoMoved) {
        props.onAlbumChange?.();
      }
      return;
    }

    const albumActionToCall = preferCopy
      ? saleVehicleAlbum.actions.copyImages
      : saleVehicleAlbum.actions.moveImages;

    albumActionToCall(sourceAlbum, vehicleImages).then((result) => {
      if (result) {
        props.onAlbumChange?.();
      }
    });
  };

  const handleSelectedItemsMove = () => {
    imageSelection.clearImageSelection();
    hideBulkActions();
  };

  // Simple local state for drag operation - no context dependency
  const [isDndSorting, setIsDndSorting] = useState(false);

  // In the Photo Guide view useGuidedAlbumDndManager owns the drops; the flat manager's ghost
  // updates and position saves would otherwise fight it over the same album.
  const initDndManager = useVehicleAlbumDndManager({
    containerId: props.saleVehicleAlbumId,
    inactiveContainerIds: props.inactiveAlbumIds ?? [],
    selectedItemIds: mergeValues(props.globallySelectedImagesIds ?? {}),
    onUpdate: isGuided ? () => undefined : handleUpdate,
    onEnd: isGuided ? () => undefined : handleEnd,
    onSelectedItemsMove: handleSelectedItemsMove,
    onDragStart: () => setIsDndSorting(true),
    onDragEnd: () => setIsDndSorting(false),
  });

  useEffect(
    function initializeMergedAlbumImagesWhenFetched() {
      if (!isFetchingAlbumImages && !isFetchingPromotionalPhotos) {
        setTimeout(() => {
          const effectivePromoPhotos = props.showPromotionalPhotos
            ? (saleVehicle?.effectivePromoPhotos ?? [])
            : [];
          const {campaignPromoPhotos, ownPromoPhotos} = splitEffectivePromoPhotos(
            effectivePromoPhotos,
            promotionalPhotos ?? []
          );
          const financingPlaceholders = isFinancingPromoEnabled
            ? buildFinancingPlaceholders(
                promotionalPhotos ?? [],
                financingPromoStatus ?? [],
                promoPhotoConfigs ?? []
              )
            : [];
          const mergedImages = mergeAlbumAndPromoImages(
            sortImages(albumImages?.map(toUnifiedAlbumImage) ?? []),
            sortImages([...campaignPromoPhotos, ...ownPromoPhotos, ...financingPlaceholders])
          );
          renderedImagesRef.current = mergedImages;
          initDndManager(mergedImages);
          setImagesToRender(mergedImages);
        });
      }
    },
    [
      albumImages,
      promotionalPhotos,
      saleVehicle?.effectivePromoPhotos,
      props.showPromotionalPhotos,
      isFinancingPromoEnabled,
      financingPromoStatus,
      promoPhotoConfigs,
      initDndManager,
      isFetchingAlbumImages,
      isFetchingPromotionalPhotos,
    ]
  );

  const {onBackgroundRemoval, saleVehicleAlbumId} = props;

  useEffect(
    function notifyBackgroundRemovalAlbumStatus() {
      if (isNotNil(albumImages)) {
        const isBackgroundRemovalInProgress = isPositive(
          filter(
            (image) =>
              image.latestFileOperation?.state === 'IN_PROGRESS' &&
              image.latestFileOperation?.type === 'REMOVE_BACKGROUND' &&
              isFalse(image.latestFileOperation?.acknowledged),
            albumImages ?? []
          ).length
        );

        const imagesWithFailedRemoveBackgroundOperation = filter(
          (image) =>
            image.latestFileOperation?.state === 'FAILED' &&
            image.latestFileOperation?.type === 'REMOVE_BACKGROUND' &&
            isFalse(image.latestFileOperation?.acknowledged),
          albumImages ?? []
        );

        if (isPositive(imagesWithFailedRemoveBackgroundOperation.length)) {
          onBackgroundRemoval?.(
            saleVehicleAlbumId,
            isBackgroundRemovalInProgress,
            reject(
              isNil,
              imagesWithFailedRemoveBackgroundOperation.map(
                (photo) => photo.latestFileOperation?.id
              )
            )
          );
        } else {
          onBackgroundRemoval?.(saleVehicleAlbumId, isBackgroundRemovalInProgress);
        }
      }
    },
    [albumImages, onBackgroundRemoval, saleVehicleAlbumId]
  );

  const areSelectedInBulk = areBulkActionsVisible && imageSelection.getSelectedImages().length > 0;

  // Album Bulk Actions Handlers
  const handleBulkActionsToggle = () => {
    if (areBulkActionsVisible) {
      imageSelection.clearImageSelection();
      setShiftSelectionAnchorId(null);
      hideBulkActions();
    } else {
      showBulkActions();
    }
  };

  const handleBulkDelete = async () => {
    const selectedImages = imageSelection.getSelectedImages();
    if (!selectedImages.length) {
      return;
    }
    await saleVehicleAlbum.actions.deleteImageFromAlbum(selectedImages);
    handleBulkActionsToggle();
    props.onAlbumChange?.();
  };

  const handleBackgroundRemoval = (imageId?: string) => {
    if (!canRemoveBackground) {
      return;
    }
    let imagesToProcess: string[];

    if (isNotNil(imageId) && !areSelectedInBulk) {
      imagesToProcess = [imageId];
    } else {
      const selectedImages = imageSelection.getSelectedImages();
      imagesToProcess = isPositive(selectedImages.length)
        ? selectedImages
        : (albumImages?.map((image) => image.id) ?? []);
    }

    openDialog(
      <BackgroundRemovalDialog
        saleVehicleId={props.saleVehicleId}
        saleVehicleAlbumId={props.saleVehicleAlbumId}
        vehiclePhotoIds={imagesToProcess}
        onConfirm={() => {
          if (areBulkActionsVisible) {
            imageSelection.clearImageSelection();
            hideBulkActions();
          }
        }}
      />,
      {
        'data-testid': suffixTestId('album-removeBackground', props),
        title: i18n.t('entity.photo.actions.removeBackground'),
      }
    );
  };

  const handleCategorySelect = (
    imageId: string | string[],
    category: VehiclePhotoCategory | null
  ) => {
    patchSaleVehiclePhotosCategory({
      saleVehicleId: props.saleVehicleId,
      saleVehicleAlbumId: props.saleVehicleAlbumId,
      patchSaleVehiclePhotosCategoryRequestBody: {
        saleVehiclePhotoIds: Array.isArray(imageId) ? imageId : [imageId],
        category,
      },
    })
      .unwrap()
      .then(() => {
        props.onAlbumChange?.();
      })
      .catch(handleApiError);
  };

  const buildImageActionsContextMenu = (image: UnifiedAlbumImage) => {
    const isProcessing =
      saleVehicleAlbum.processingImageIds.includes(image.id) ||
      image.latestFileOperation?.state === 'IN_PROGRESS';

    return match(image)
      .returnType<DropdownItemProps[]>()
      .with({isCampaignPhoto: true}, () => [])
      .with({isPromoPhoto: true}, () =>
        buildArray<DropdownItemProps>([]).when(canDeleteVehicleAlbumPhoto, {
          label: i18n.t('general.actions.delete'),
          prefix: <Icon value="action/delete" />,
          isDisabled: isProcessing || saleVehicleAlbum.areActionsInProgress,
          onClick: () => {
            saleVehicleAlbum.actions.deletePromotionalPhotoFromAlbum(image.id).then((result) => {
              if (result) {
                props.onAlbumChange?.();
              }
            });
          },
        })
      )
      .otherwise(() => {
        const isExterior = image.category === 'EXTERIOR';
        const isInterior = image.category === 'INTERIOR';

        return buildArray<DropdownItemProps>([])
          .when(
            canEditPhotos &&
              props.isActiveSaleVehicle &&
              !areBulkActionsVisible &&
              !image.isCoverPhoto,
            () => ({
              label: i18n.t('general.actions.setAsCoverPhoto'),
              prefix: <Icon value="image/photo" />,
              onClick: () => {
                saleVehicleAlbum.actions.setImageAsCoverPhoto(image.id).then((result) => {
                  if (result) {
                    props.onAlbumChange?.();
                  }
                });
              },
            })
          )
          .when(canEditPhotos, {
            label: i18n.t('general.actions.rotateRight'),
            prefix: <Icon value="action/rotateRight" />,
            isDisabled: isProcessing,
            onClick: () => {
              saleVehicleAlbum.actions
                .rotateImage({
                  uuid: areSelectedInBulk ? imageSelection.getSelectedImages() : image.id,
                  angle: 90,
                })
                .then((result) => {
                  if (areBulkActionsVisible) {
                    handleBulkActionsToggle();
                  }
                  if (result) {
                    props.onAlbumChange?.();
                  }
                });
            },
          })
          .when(canEditPhotos, {
            label: i18n.t('general.actions.rotateLeft'),
            prefix: <Icon value="action/rotateLeft" />,
            isDisabled: isProcessing,
            onClick: () => {
              saleVehicleAlbum.actions
                .rotateImage({
                  uuid: areSelectedInBulk ? imageSelection.getSelectedImages() : image.id,
                  angle: 270,
                })
                .then((result) => {
                  if (areBulkActionsVisible) {
                    handleBulkActionsToggle();
                  }
                  if (result) {
                    props.onAlbumChange?.();
                  }
                });
            },
          })
          .add({
            label: isExterior
              ? i18n.t('entity.photo.actions.unmarkExterior')
              : i18n.t('entity.photo.actions.markExterior'),
            prefix: <Icon value={isExterior ? 'action/unmark_exterior' : 'action/mark_exterior'} />,
            onClick: () => handleCategorySelect(image.id, isExterior ? null : 'EXTERIOR'),
          })
          .add({
            label: isInterior
              ? i18n.t('entity.photo.actions.unmarkInterior')
              : i18n.t('entity.photo.actions.markInterior'),
            prefix: <Icon value={isInterior ? 'action/unmark_interior' : 'action/mark_interior'} />,
            onClick: () => handleCategorySelect(image.id, isInterior ? null : 'INTERIOR'),
          })
          .when(canRemoveBackground, {
            label: i18n.t('entity.photo.actions.removeBackground'),
            prefix: <Icon value="action/magic_wand" />,
            isDisabled: isProcessing,
            onClick: () => handleBackgroundRemoval(image.id),
          })
          .when(canDeleteVehicleAlbumPhoto && !areBulkActionsVisible, () => ({
            label: i18n.t('general.actions.delete'),
            prefix: <Icon value="action/delete" />,
            isDisabled: isProcessing,
            onClick: () => {
              saleVehicleAlbum.actions.deleteImageFromAlbum(image.id).then(() => {
                props.onAlbumChange?.();
              });
            },
          }));
      });
  };

  const watermarkOptions =
    watermarks?.map((watermark) => ({
      label: watermark.name,
      value: watermark.id,
    })) ?? [];

  const fallbackWatermarkIds = isNotNilOrEmpty(watermarkOptions)
    ? [watermarkOptions[0]?.value]
    : [];

  const handleApplyWatermarks = (watermarkIds: string[]) => {
    patchWatermark({
      vehicleId: saleVehicle?.vehicleId ?? '',
      patchSaleVehicleWatermarkV2RequestBody: {watermarkIds},
    })
      .unwrap()
      .then(() => props.onAlbumChange?.())
      .catch(handleWatermarkPatchError);
  };

  const selectedImageDetails = imageSelection
    .getSelectedImages()
    .map((id) => albumImages?.find((image) => image.id === id))
    .filter(Boolean);

  const hasSelectedImageWithCategory = selectedImageDetails.some(
    (image) => image?.category !== null
  );

  // A financing placeholder has no file to show, so the lightbox skips it.
  const lightboxImages = imagesToRender.filter((image) => isNil(image.financingPlaceholder));

  const renderAlbumImage: RenderAlbumImage = (image, index, options) => (
    <AlbumImage
      key={image.isDirty ? `dirty-${image.id}` : image.id}
      id={
        options.dndId ?? buildSortableItemId(image.id, props.saleVehicleAlbumId, image.url, index)
      }
      src={prepareAlbumImageUrl(image.url)}
      placeholder={
        isNotNil(image.financingPlaceholder) ? (
          <FinancingPlaceholderTile
            placeholder={image.financingPlaceholder}
            data-testid={suffixTestId(options.testId ?? `album-images-[${index}]`, props)}
          />
        ) : undefined
      }
      isProcessing={isImageProcessing(
        saleVehicleAlbum.processingImageIds.includes(image.id),
        image.latestFileOperation
      )}
      isDisabledDndDrop={
        options.isDisabledDndDrop ?? (!props.canUpload || !canEditPhotos || image.isCampaignPhoto)
      }
      isDisabledDndDrag={
        options.isDisabledDndDrag ?? (image.isCampaignPhoto || isNotNil(image.financingPlaceholder))
      }
      isAlbumLocked={image.isPromoPhoto || isNotNil(image.financingPlaceholder)}
      isDropTarget={options.isDropTarget}
      caption={options.caption}
      isGhost={image.isGhost}
      isCoverPhoto={image.isCoverPhoto}
      isPromoPhoto={image.isPromoPhoto}
      isCampaignPhoto={image.isCampaignPhoto}
      isFinancingPromoPhoto={
        isNotNil(image.financingPlaceholder) || isFinancingPromoPhoto(image, financingPromoStatus)
      }
      isLoading={image.isLoading}
      category={image.category}
      isSelected={imageSelection.isImageSelected(image.id)}
      isSelectable={areBulkActionsVisible && !image.isPromoPhoto}
      data-testid={suffixTestId(options.testId ?? `album-images-[${index}]`, props)}
      photoCheckerEval={
        isPhotoCheckerEnabled && !image.isPromoPhoto
          ? matchPhotoEval(image.url, props.photoEvalMap ?? {})
          : undefined
      }
      onClick={(event) => {
        if (isNotNil(image.financingPlaceholder)) {
          return;
        }
        if (areBulkActionsVisible && !image.isPromoPhoto) {
          const anchorIndex = isNotNil(shiftSelectionAnchorId)
            ? options.list.findIndex((img) => img.id === shiftSelectionAnchorId)
            : -1;
          if (event.shiftKey && anchorIndex >= 0) {
            const currentIndex = index;
            const start = Math.min(anchorIndex, currentIndex);
            const end = Math.max(anchorIndex, currentIndex);

            const rangeToSelect = options.list
              .slice(start, end + 1)
              .filter((img) => !img.isPromoPhoto)
              .map((img) => img.id);

            imageSelection.selectImagesInBulk(rangeToSelect);
          } else {
            imageSelection.toggleImageSelection(image.id);
            setShiftSelectionAnchorId(image.id);
          }
        } else if (!areBulkActionsVisible) {
          onOpen(lightboxImages.findIndex((lightboxImage) => lightboxImage.id === image.id));
        }
      }}
      onContextMenu={(event) => {
        if (image.isCampaignPhoto || isNotNil(image.financingPlaceholder)) {
          return;
        }

        openDropdown(
          event,
          <>
            {image.isPromoPhoto || !canEditPhotos
              ? null
              : props.buildAlbumActionsContextMenu?.({
                  sourceAlbumId: props.saleVehicleAlbumId,
                  isSalesAlbum: !!props.isSalesAlbum,
                  images: areSelectedInBulk ? imageSelection.getSelectedImages() : [image.id],
                  isProcessing:
                    saleVehicleAlbum.processingImageIds.includes(image.id) ||
                    image.latestFileOperation?.state === 'IN_PROGRESS',
                  onEventFired: () => {
                    closeDropdown(ALBUM_IMAGE_CONTEXT_MENU_ID);
                  },
                  onSuccess: () => {
                    if (areBulkActionsVisible) {
                      handleBulkActionsToggle();
                    }
                  },
                })}
            <Show when={canEditPhotoGuide && !areSelectedInBulk && !image.isPromoPhoto}>
              <PhotoGuideContextMenuItems
                image={image}
                photoGuideItem={photoGuide?.items.find(
                  (item) => item.saleVehiclePhotoId === image.id
                )}
                dropdownId={ALBUM_IMAGE_CONTEXT_MENU_ID}
                actions={photoGuideActions}
                data-testid={suffixTestId('album-image-context-menu-photoGuide', props)}
              />
            </Show>
            {buildImageActionsContextMenu(image).map((item, itemIndex) => (
              <DropdownItem
                key={typeof item.label === 'string' ? item.label : itemIndex}
                {...item}
              />
            ))}
          </>,
          {
            id: ALBUM_IMAGE_CONTEXT_MENU_ID,
          }
        );
      }}
    />
  );

  const uploadTiles = (
    <>
      {saleVehicleAlbum.filePreview.list().map((preview, index) => (
        <AlbumImage
          id={`preview-${index}`}
          data-testid={suffixTestId(`album-imagePreviews-[${index}]`, props)}
          key={preview.id}
          src={preview.uri}
          isLoading
          isDisabledDndDrop
          isDisabledDndDrag
        />
      ))}
      <Show when={isUploadAllowed && !isDndSorting}>
        <Box width="100%" height="100%" ratio="4 / 3">
          <Upload
            isDisabled={isUploadInProgress}
            data-testid={suffixTestId('album', props)}
            type="card"
            size="default"
            accept={ALLOWED_MIME_TYPES_TO_UPLOAD}
            isMultiple
            shouldValidateAccept
            uploadIcon="image/photo_camera"
            onStart={(file) => {
              setIsUploadInProgress(true);
              saleVehicleAlbum.filePreview.show(file);
            }}
            onSuccess={() => {
              setIsUploadInProgress(false);
              props.startFinancingRenderWindow?.();
              props.onAlbumChange?.();
            }}
            onError={(...args) => {
              setIsUploadInProgress(false);
              saleVehicleAlbum.handlers.handleUploadError(...args);
            }}
            customRequest={({file, onSuccess, onError}) => {
              if (file instanceof File) {
                saleVehicleAlbum.actions.addImageToAlbum({
                  file,
                  onSuccess,
                  onError,
                });
              }
              // else should be string, which should not happen
            }}
          />
        </Box>
      </Show>
      <Show when={isUploadAllowed && isDndSorting}>
        <Box width="100%" height="100%" ratio="4 / 3" />
      </Show>
    </>
  );

  return (
    <Card
      data-testid={suffixTestId('album', props)}
      title={props.title}
      subtitle={
        isNotNil(props.createdAt)
          ? formatDateTime('dateShort', parseDate(props.createdAt))
          : undefined
      }
      actions={buildArray<Action>()
        .when(areBulkActionsVisible, {
          'data-testid': suffixTestId('album-selectImagesInBulk', props),
          type: 'button',
          title: i18n.t('general.actions.selectAll'),
          variant: 'ghostLink',
          leftIcon: 'action/done_all',
          onClick: () =>
            imageSelection.selectImagesInBulk(albumImages?.map((image) => image.id) ?? []),
        })
        .when(isPositive(albumImages?.length), {
          'data-testid': suffixTestId('album-removeBackground', props),
          type: 'buttonWithTooltip',
          title: i18n.t('entity.photo.actions.removeBackground'),
          variant: 'ghostLink',
          leftIcon: 'action/magic_wand',
          isDisabled: !canRemoveBackground,
          onClick: () => handleBackgroundRemoval(),
          tooltip: {
            placement: 'top',
            label: i18n.t('general.labels.noPermission'),
            isDisabled: canRemoveBackground,
          },
        })
        .when(isGuided && missingRequiredPhotoCount > 0, {
          type: 'custom',
          component: (
            <PhotoGuideMissingRequiredFlag
              count={missingRequiredPhotoCount}
              data-testid={suffixTestId('album-missingRequiredPhotos', props)}
            />
          ),
        })
        .when(canEditPhotoGuide && !areBulkActionsVisible, {
          'data-testid': suffixTestId('album-arrangeByPhotoGuide', props),
          type: 'button',
          variant: 'ghostLink',
          title: i18n.t('entity.photoGuide.actions.arrangeByPhotoGuide'),
          leftIcon: 'content/sort',
          isLoading: photoGuideActions.isArranging,
          onClick: photoGuideActions.arrangeByPhotoGuide,
        })
        .when(areBulkActionsVisible, {
          title: i18n.t('entity.photo.actions.markInterior'),
          type: 'button',
          variant: 'ghostLink',
          leftIcon: 'action/mark_interior',
          onClick: () => handleCategorySelect(imageSelection.getSelectedImages(), 'INTERIOR'),
        })
        .when(areBulkActionsVisible, {
          title: i18n.t('entity.photo.actions.markExterior'),
          type: 'button',
          variant: 'ghostLink',
          leftIcon: 'action/mark_exterior',
          onClick: () => handleCategorySelect(imageSelection.getSelectedImages(), 'EXTERIOR'),
        })
        .when(
          areBulkActionsVisible &&
            isPositive(imageSelection.getSelectedImages().length) &&
            hasSelectedImageWithCategory,
          {
            title: i18n.t('entity.photo.actions.unmark'),
            type: 'button',
            variant: 'ghostLink',
            leftIcon: 'action/unmark_exterior',
            iconColor: 'general.accent',
            onClick: () => handleCategorySelect(imageSelection.getSelectedImages(), null),
          }
        )
        .when(!areBulkActionsVisible && props.showPromotionalPhotos, {
          'data-testid': suffixTestId('album-addPromotionalPhotos', props),
          type: 'button',
          variant: 'ghostLink',
          title: i18n.t('entity.photo.actions.addPromoPhotos'),
          leftIcon: 'image/add_photo_alternate',
          onClick: () => {
            saleVehicleAlbum.actions.addPromotionalPhotosToAlbum().then((result) => {
              if (result) {
                props.onAlbumChange?.();
              }
            });
          },
          isDisabled: saleVehicleAlbum.areActionsInProgress,
        })
        .when(
          isVgpCertificateAvailable &&
            props.isSalesAlbum &&
            props.isActiveSaleVehicle &&
            !areBulkActionsVisible,
          {
            'data-testid': suffixTestId('album-createCertificate', props),
            type: 'button',
            variant: 'ghostLink',
            title: i18n.t('entity.vehicle.actions.createVgpCertificate'),
            leftIcon: 'toggle/star',
            isLoading: isGeneratingVgpCertificate,
            onClick: () => {
              generateVgpCertificate({vehicleId: props.vehicleId})
                .unwrap()
                .then(() => {
                  showNotification.success(
                    i18n.t('entity.vehicle.notifications.certificateGeneratedSuccessfully')
                  );
                  props.startFinancingRenderWindow?.();
                  dispatch(
                    saleVehiclePhotoApi.util.invalidateTags([
                      {type: 'SaleVehicleAlbumPhotos', id: props.saleVehicleAlbumId},
                    ])
                  );
                  props.onAlbumChange?.();
                })
                .catch(handleApiError);
            },
          }
        )
        .when(
          canDeleteVehicleAlbumPhoto &&
            areBulkActionsVisible &&
            isPositive(imageSelection.getSelectedImages().length),
          {
            'data-testid': suffixTestId('album-handleBulkDelete', props),
            type: 'button',
            title: i18n.t('general.actions.delete'),
            variant: 'dangerGhost',
            leftIcon: 'action/delete',
            onClick: handleBulkDelete,
          }
        )
        .when(props.canDelete && !areBulkActionsVisible, {
          'data-testid': suffixTestId('album-deleteAlbum', props),
          type: 'button',
          title: i18n.t('entity.vehicle.albums.delete'),
          variant: 'dangerGhost',
          leftIcon: 'action/delete',
          onClick: () => {
            openDeleteDialog({
              text: i18n.t('entity.vehicle.albums.deleteQuestion'),
              onConfirm: () => {
                saleVehicleAlbum.actions.deleteAlbum().then((result) => {
                  if (result) {
                    props.onAlbumChange?.();
                  }
                });
              },
            });
          },
        })
        .add({
          'data-testid': suffixTestId('album-handleBulkActionsToggle', props),
          type: 'button',
          title: areBulkActionsVisible
            ? i18n.t('general.actions.cancel')
            : i18n.t('general.actions.bulkActions'),
          variant: 'ghostLink',
          leftIcon: 'image/filter_none',
          onClick: handleBulkActionsToggle,
        })
        .add({
          'data-testid': suffixTestId('album-downloadAll', props),
          type: 'button',
          title: i18n.t('general.actions.downloadAll'),
          variant: 'ghostLink',
          leftIcon: 'file/download',
          isLoading: isBatchDownloadLoading,
          isDisabled: albumImages?.length === 0,
          onClick: () => {
            handleSaleVehicleDownload({
              saleVehicleId: props.saleVehicleId,
              albumId: props.saleVehicleAlbumId,
              applyWatermarks: shouldApplyWatermark,
            }).then((event) => {
              if (isNotNil(event?.data)) {
                openDialog(
                  <DownloadDialog
                    fileName={event?.data?.bulkName}
                    fileCount={event?.data?.objects.length}
                  />,
                  {
                    id: 'download-dialog',
                    title: i18n.t('entity.vehicle.labels.downloadPhotos'),
                    size: 'small',
                  }
                );
              }
            });
          },
        })}
    >
      <Show when={isNotNil(props.onReusePreviousSalePhotos) && isUploadAllowed}>
        <Box paddingBottom={4}>
          <Alert
            type="inline"
            variant="info"
            title={i18n.t('entity.vehicle.albums.previousSalePhotosAvailable')}
            data-testid={suffixTestId('previousSalePhotosAlert', props)}
            hyperlinks={[
              {
                size: 'small',
                title: i18n.t('entity.vehicle.albums.reusePreviousSalePhotos'),
                isDisabled: props.isReusingPreviousSalePhotos,
                onClick: () => props.onReusePreviousSalePhotos?.(),
                leftIcon: 'content/file_copy',
              },
            ]}
          />
        </Box>
      </Show>
      <Show when={promotionalPhotos?.some((image) => image.position !== image.positionInSettings)}>
        <Box paddingBottom={4}>
          <Alert
            type="inline"
            title={i18n.t('entity.photo.labels.promoPhotoDifferentOrder')}
            data-testid={suffixTestId('promoPhotoAlert', props)}
            hyperlinks={[
              {
                size: 'small',
                title: i18n.t('entity.photo.actions.setDefaultOrder'),
                onClick: () => {
                  saleVehicleAlbum.actions.resetPromotionalPhotos().then((result) => {
                    if (result) {
                      props.onAlbumChange?.();
                    }
                  });
                },
                leftIcon: 'navigation/refresh',
              },
            ]}
          />
        </Box>
      </Show>
      <Show when={saleVehicle && props.canApplyWatermark && watermarks && watermarks?.length > 0}>
        <Box paddingBottom={4}>
          <HStack spacing={4} align="center" wrap>
            <Switch
              label={i18n.t('entity.watermark.labels.applyWatermark')}
              value={shouldApplyWatermark}
              isDisabled={isPatchingWatermark}
              onChange={(value) => handleApplyWatermarks(value ? fallbackWatermarkIds : [])}
              data-testid={suffixTestId(`album-images-apply-watermark-switch`, props)}
            />
            <Show when={shouldApplyWatermark}>
              <Box minWidth={64}>
                <VehicleWatermarkChoice
                  options={watermarkOptions}
                  value={selectedWatermarkIds}
                  effectiveWatermarkIds={saleVehicle?.effectiveWatermarkIds ?? []}
                  onChange={handleApplyWatermarks}
                  isLoading={isPatchingWatermark}
                  data-testid={suffixTestId(`album-images-apply-watermark-select`, props)}
                />
              </Box>
            </Show>
          </HStack>
        </Box>
      </Show>
      <Hide when={imagesToRender.length || props.isActiveSaleVehicle || props.canUpload}>
        <Center width="100%" height={IMAGE_SIZE.height}>
          <VStack align="center">
            <Icon value="image/image_alt" color="text.tertiary" size={8} />
            <Space vertical={2} />
            <Heading size={5} color="tertiary">
              {i18n.t('entity.vehicle.albums.empty')}
            </Heading>
          </VStack>
        </Center>
      </Hide>
      <Hide when={imagesToRender.length === 0 && !props.isActiveSaleVehicle && !props.canUpload}>
        <Show when={isGuided && isNotNil(photoGuide) ? photoGuide : null}>
          {(guide) => (
            <GuidedAlbumContent
              data-testid={suffixTestId('album-photoGuide', props)}
              saleVehicleAlbumId={props.saleVehicleAlbumId}
              photoGuide={guide}
              images={guidedImages}
              fetchedAt={{photoGuide: photoGuideFetchedAt, albumPhotos: albumImagesFetchedAt}}
              inactiveContainerIds={props.inactiveAlbumIds ?? []}
              canEdit={canEditPhotoGuide}
              isSyncing={isFetchingPhotoGuide || isFetchingAlbumImages}
              isLoading={isLoadingAlbumContent}
              isError={hasAlbumContentError}
              renderAlbumImage={renderAlbumImage}
              backlogTrailingTiles={uploadTiles}
              actions={photoGuideActions}
              albumActions={saleVehicleAlbum.actions}
              selectedImageIds={mergeValues(props.globallySelectedImagesIds ?? {})}
              albumPhotoCount={albumImages?.length ?? 0}
              onAlbumChange={props.onAlbumChange}
            />
          )}
        </Show>
        <Show when={!isGuided}>
          <AlbumPhotoGrid
            containerId={props.saleVehicleAlbumId}
            sortableItemIds={imagesToRender.flatMap((image, index) =>
              image.isCampaignPhoto
                ? []
                : [buildSortableItemId(image.id, props.saleVehicleAlbumId, image.url, index)]
            )}
            isDropDisabled={!props.canUpload || !canEditPhotos}
            isLoading={isLoadingAlbumContent}
            isError={hasAlbumContentError}
          >
            {imagesToRender.map((image, index) =>
              renderAlbumImage(image, index, {list: imagesToRender})
            )}
            {uploadTiles}
          </AlbumPhotoGrid>
        </Show>
      </Hide>
      <Lightbox
        data-testid={suffixTestId('lightbox', props)}
        controls={lightboxControls}
        images={lightboxActionsFactory.addFullActions(
          lightboxImages.map((image) => ({
            ...image,
            url: image.isCampaignPhoto ? image.url : image.url.replace('/resize/', '/get/'),
            resizeUrl: image.url,
          })),
          // Promo tiles carry a promo link id, not a photo id; the tile context menu removes them.
          (image) =>
            buildArray<LightboxAction>().when(canDeleteVehicleAlbumPhoto && !image.isPromoPhoto, {
              id: 'deleteBtn',
              icon: 'action/delete',
              onClick: () => openLightboxDeleteDialog(image.id),
            })
        )}
      />
      <DeleteDialog
        isOpen={isLightboxDeleteDialogOpen}
        onClose={closeLightboxDeleteDialog}
        onConfirm={() => {
          if (imageToDeleteId) {
            saleVehicleAlbum.actions.deleteImageFromAlbum(imageToDeleteId).then(() => {
              props.onAlbumChange?.();
            });
          }
        }}
        isInLightbox
      />
      {lightboxActionsFactory.photoDownloadSizeDialog}
    </Card>
  );
}

const mergeValues = (obj: Record<string, string[]>) => flatten(values(obj));

export const Album = memo(AlbumComponent);
